import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { DomainRouterGuard } from "@/components/routing/DomainRouterGuard";
import IndexingPolicy from "@/components/seo/IndexingPolicy";
import { AuthProvider } from "@/auth/auth-context";
import { ProtectedRoute } from "@/auth/protected-route";
import { PublicRoute } from "@/auth/public-route";
import AffiliateAttributionCapture from "@/components/marketing/AffiliateAttributionCapture";
import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import Affiliate from "./pages/Affiliate";
import Login from "./pages/Login";
import CreateAccount from "./pages/CreateAccount";
import Payment from "./pages/Payment";
import Success from "./pages/Success";
import AcceptInvite from "./pages/AcceptInvite";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import SmsConsent from "./pages/SmsConsent";
import SmsOptInProof from "./pages/SmsOptInProof";
import Dashboard from "./pages/Index";
import Schedule from "./pages/Schedule";
import Tickets from "./pages/Tickets";
import CreateTicket from "./pages/CreateTicket";
import TicketDetail from "./pages/TicketDetail";
import ExternalApproval from "./pages/ExternalApproval";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Clients from "./pages/Clients";
import ClientDetail from "./pages/ClientDetail";
import Approvals from "./pages/Approvals";
import BillingExport from "./pages/BillingExport";
import Reports from "./pages/Reports";
import Team from "./pages/Team";
import Settings from "./pages/Settings";
import UpgradePlan from "./pages/UpgradePlan";
import NotFound from "./pages/NotFound";
import { useAuth } from "@/auth/auth-context";
import { isDashboardDomainHost, isManagedDomainHost, ROUTE_PATHS } from "@/routing/domainRoutes";

function DomainAwareHomeRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">Loading session...</div>;
  }

  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const shouldRenderDashboard =
    (isManagedDomainHost(hostname) && isDashboardDomainHost(hostname)) ||
    (!isManagedDomainHost(hostname) && Boolean(user));

  if (shouldRenderDashboard) {
    return (
      <ProtectedRoute>
        <AppLayout>
          <Dashboard />
        </AppLayout>
      </ProtectedRoute>
    );
  }

  return <Landing />;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AuthProvider>
        <BrowserRouter>
          <AffiliateAttributionCapture />
          <IndexingPolicy />
          <Routes>
            <Route element={<DomainRouterGuard />}>
              {/* Public pages */}
              <Route path={ROUTE_PATHS.landing} element={<DomainAwareHomeRoute />} />
              <Route path={ROUTE_PATHS.pricing} element={<Pricing />} />
              <Route path={ROUTE_PATHS.affiliate} element={<Affiliate />} />
              <Route path={ROUTE_PATHS.login} element={<PublicRoute><Login /></PublicRoute>} />
              <Route path={ROUTE_PATHS.createAccount} element={<PublicRoute><CreateAccount /></PublicRoute>} />
              <Route path={ROUTE_PATHS.payment} element={<PublicRoute><Payment /></PublicRoute>} />
              <Route path={ROUTE_PATHS.success} element={<PublicRoute><Success /></PublicRoute>} />
              <Route path={ROUTE_PATHS.acceptInvite} element={<PublicRoute><AcceptInvite /></PublicRoute>} />
              <Route path={ROUTE_PATHS.terms} element={<Terms />} />
              <Route path={ROUTE_PATHS.privacy} element={<Privacy />} />
              <Route path={ROUTE_PATHS.smsConsent} element={<SmsConsent />} />
              <Route
                path={ROUTE_PATHS.smsConsentLegacy}
                element={<Navigate to={ROUTE_PATHS.smsConsent} replace />}
              />
              <Route path={ROUTE_PATHS.smsOptInProof} element={<SmsOptInProof />} />
              <Route path={ROUTE_PATHS.approve} element={<ExternalApproval />} />

              {/* App pages with layout */}
              <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                <Route path={ROUTE_PATHS.dashboardLegacy} element={<Dashboard />} />
                <Route path={ROUTE_PATHS.schedule} element={<Schedule />} />
                <Route path={ROUTE_PATHS.tickets} element={<Tickets />} />
                <Route path={ROUTE_PATHS.ticketNew} element={<CreateTicket />} />
                <Route path={ROUTE_PATHS.ticketDetail} element={<TicketDetail />} />
                <Route path={ROUTE_PATHS.projects} element={<Projects />} />
                <Route path={ROUTE_PATHS.projectDetail} element={<ProjectDetail />} />
                <Route path={ROUTE_PATHS.clients} element={<Clients />} />
                <Route path={ROUTE_PATHS.clientDetail} element={<ClientDetail />} />
                <Route path={ROUTE_PATHS.approvals} element={<Approvals />} />
                <Route path={ROUTE_PATHS.billing} element={<BillingExport />} />
                <Route path={ROUTE_PATHS.reports} element={<Reports />} />
                <Route path={ROUTE_PATHS.team} element={<Team />} />
                <Route path={ROUTE_PATHS.settings} element={<Settings />} />
                <Route path={ROUTE_PATHS.upgradePlan} element={<UpgradePlan />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
